import React, { useMemo } from "react";
import useSelect from "../hooks/useSelect";
import { cn } from "components/lib/utils";
import { getColorStyle } from "utils/tagColor";
import {
  Select as SSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";

// Props: { options, initialValue, onOptionChange }
// options shape: [{ value, label, color }] — color is a hex string (arbitrary,
// per-record data like Tag colors), applied as inline style, not a Tailwind class.
function Select({ options, initialValue, onOptionChange }) {
  const { selectedOption, handleOptionChange } = useSelect(initialValue, onOptionChange);

  // Radix can't use "" as a value; use "all" and map back to "" for your state/backend
  const mapOut = (v) => (v === "" || v == null ? "all" : String(v));
  const mapIn = (v) => (v === "all" ? "" : v);

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) => ({
      ...opt,
      value: mapOut(opt.value),
      color: opt.color ?? null,
    }));
  }, [options]);

  // keep your hook working (event-like object)
  const handleRadixChange = (val) => handleOptionChange({ target: { value: mapIn(val) } });

  const currentUIValue = mapOut(selectedOption);

  const selectedColor =
    normalizedOptions.find((o) => o.value === currentUIValue)?.color || null;
  const selectedColorStyle = getColorStyle(selectedColor);

  return (
    <div className="w-full">
      <SSelect value={currentUIValue} onValueChange={handleRadixChange}>
        <SelectTrigger
          className="w-full"
          style={selectedColorStyle || undefined}
          aria-label="בחר סטטוס"
        >
          <SelectValue placeholder="בחר" />
        </SelectTrigger>

        <SelectContent position="popper" className="max-h-64">
          {normalizedOptions.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              className={cn("focus:bg-accent focus:text-accent-foreground")}
            >
              <span className="flex items-center gap-2">
                {opt.color && (
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: opt.color }}
                    aria-hidden="true"
                  />
                )}
                {opt.label}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </SSelect>
    </div>
  );
}

export default Select;
